/**
 * Pure layout and input rules for the scenario components, kept apart from
 * the components so they are testable and fast refresh keeps working.
 */
import type {
  ScenarioBridgeStep,
  ScenarioLever,
  ScenarioTemplate,
} from "../../services/api";

export function defaultLevers(template: ScenarioTemplate): Record<string, number> {
  return Object.fromEntries(template.levers.map((lever) => [lever.id, lever.default]));
}

/** Keeps a typed value inside the lever's bounds and on its step grid. */
export function normaliseLever(lever: ScenarioLever, raw: number): number {
  if (!Number.isFinite(raw)) return lever.default;
  const bounded = Math.min(lever.maximum, Math.max(lever.minimum, raw));
  const steps = Math.round((bounded - lever.minimum) / lever.step);
  const snapped = lever.minimum + steps * lever.step;
  // Removes binary noise such as 2.5000000000000004 from step arithmetic.
  return Number(snapped.toFixed(6));
}

export interface BridgeBar {
  label: string;
  value: number;
  kind: ScenarioBridgeStep["kind"];
  start: number;
  end: number;
}

export interface BridgeLayout {
  bars: BridgeBar[];
  low: number;
  high: number;
  zoomed: boolean;
}

/**
 * Lays the steps out as a waterfall. Totals stand on their own; each step
 * starts where the running total left off.
 *
 * When the totals dwarf the steps, a zero-based axis would draw a $4M change
 * against $200M as a hairline. The axis is then zoomed to the range that
 * matters and labelled as such, which is how finance teams read a bridge.
 */
export function layoutBridge(steps: ScenarioBridgeStep[]): BridgeLayout {
  let running = 0;
  const bars = steps.map<BridgeBar>((step) => {
    if (step.kind === "total") {
      running = step.value;
      return { ...step, start: 0, end: step.value };
    }
    const start = running;
    running += step.value;
    return { ...step, start, end: running };
  });
  const points = bars.flatMap((bar) =>
    bar.kind === "total" ? [bar.end] : [bar.start, bar.end],
  );
  if (points.length === 0) return { bars, low: 0, high: 1, zoomed: false };
  const minimum = Math.min(...points);
  const maximum = Math.max(...points);
  const span = maximum - minimum;
  const zoomed = minimum > 0 && span > 0 && span < maximum * 0.5;
  const low = zoomed ? Math.max(0, minimum - span * 0.6) : Math.min(0, minimum);
  const high = Math.max(maximum, low + 1);
  return { bars, low, high, zoomed };
}

/** Shade from 0 to 1 relative to the grid's own range, or null for no value. */
export function shade(
  value: number | null,
  minimum: number,
  maximum: number,
): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  if (maximum === minimum) return 0.5;
  return (value - minimum) / (maximum - minimum);
}
